import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, Loader2, History, Download, ArrowLeftRight } from "lucide-react";
import { User } from "@supabase/supabase-js";

interface Version {
  id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  version: number;
  created_at: string;
}

interface AttachmentVersionHistoryProps {
  attachmentId: string;
  claimId: string;
  currentVersion: number;
  fileName: string;
  user: User;
  onNewVersion: () => void;
  onCompare: (version1: Version, version2: Version) => void;
}

export const AttachmentVersionHistory = ({
  attachmentId,
  claimId,
  currentVersion,
  fileName,
  user,
  onNewVersion,
  onCompare
}: AttachmentVersionHistoryProps) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState<Version[]>([]);

  useEffect(() => {
    fetchVersions();
  }, [attachmentId]);

  const fetchVersions = async () => {
    try {
      const { data, error } = await supabase
        .from("claim_attachments")
        .select("id, file_name, file_path, file_size, version, created_at")
        .or(`id.eq.${attachmentId},parent_attachment_id.eq.${attachmentId}`)
        .order("version", { ascending: false });

      if (error) throw error;

      setVersions(data || []);
    } catch (error: any) {
      console.error("Error fetching versions:", error);
      toast.error("Failed to load version history");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxSize = 20 * 1024 * 1024;

      if (file.size > maxSize) {
        toast.error("File size must be less than 20MB");
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUploadNewVersion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) return;

    setUploading(true);
    try {
      // Upload file to storage
      const fileExt = selectedFile.name.split(".").pop();
      const filePath = `${claimId}/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from("claim-attachments")
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      // Create new version record
      const { error: dbError } = await supabase.from("claim_attachments").insert({
        claim_id: claimId,
        file_name: fileName,
        file_path: filePath,
        file_type: selectedFile.type,
        file_size: selectedFile.size,
        uploaded_by: user.id,
        version: currentVersion + 1,
        parent_attachment_id: attachmentId
      });

      if (dbError) throw dbError;

      toast.success("New version uploaded");
      setSelectedFile(null);
      setShowUpload(false);
      const fileInput = document.getElementById("version-upload") as HTMLInputElement;
      if (fileInput) fileInput.value = "";
      fetchVersions();
      onNewVersion();
    } catch (error: any) {
      toast.error(error.message || "Failed to upload new version");
      console.error("Error uploading version:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadVersion = async (version: Version) => {
    try {
      const { data, error } = await supabase.storage
        .from("claim-attachments")
        .download(version.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName} (v${version.version})`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error("Failed to download version");
      console.error("Error downloading version:", error);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const handleVersionSelect = (version: Version) => {
    if (selectedForCompare.find(v => v.id === version.id)) {
      setSelectedForCompare(prev => prev.filter(v => v.id !== version.id));
    } else {
      if (selectedForCompare.length >= 2) {
        toast.error("You can only compare two versions at a time");
        return;
      }
      setSelectedForCompare(prev => [...prev, version]);
    }
  };

  const handleCompare = () => {
    if (selectedForCompare.length !== 2) {
      toast.error("Please select exactly two versions to compare");
      return;
    }
    
    const [v1, v2] = selectedForCompare.sort((a, b) => a.version - b.version);
    onCompare(v1, v2);
    setSelectedForCompare([]);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Version History (v{currentVersion})
          </CardTitle>
          <div className="flex gap-2">
            {selectedForCompare.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCompare}
                disabled={selectedForCompare.length !== 2}
              >
                <ArrowLeftRight className="h-4 w-4 mr-2" />
                Compare ({selectedForCompare.length}/2)
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowUpload(!showUpload)}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload New Version
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload New Version Form */}
        {showUpload && (
          <form onSubmit={handleUploadNewVersion} className="border rounded-lg p-4 space-y-3">
            <div>
              <Label htmlFor="version-upload">Select File</Label>
              <Input
                id="version-upload"
                type="file"
                onChange={handleFileChange}
                className="mt-1"
                required
              />
              {selectedFile && (
                <p className="text-sm text-muted-foreground mt-2">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={uploading || !selectedFile}>
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    Upload Version {currentVersion + 1}
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowUpload(false);
                  setSelectedFile(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Version List */}
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : versions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No version history available
          </p>
        ) : (
          <div className="space-y-2">
            {versions.length > 1 && (
              <p className="text-xs text-muted-foreground mb-2">
                Click on versions to select them for comparison
              </p>
            )}
            {versions.map((version) => {
              const isSelected = selectedForCompare.find(v => v.id === version.id);
              return (
                <div
                  key={version.id}
                  className={`flex items-center justify-between border rounded-lg p-3 cursor-pointer transition-colors ${
                    isSelected ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                  onClick={() => versions.length > 1 && handleVersionSelect(version)}
                >
                  <div>
                    <p className="text-sm font-medium">
                      Version {version.version}
                      {version.version === currentVersion && (
                        <span className="ml-2 text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                          Current
                        </span>
                      )}
                      {isSelected && (
                        <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-1 rounded">
                          Selected
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(version.created_at).toLocaleString()} • {formatFileSize(version.file_size)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadVersion(version);
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
